<template>
  <ag-grid-vue ref="table" style="width: 100%; height: 100%" class="ag-theme-material" :rowData="citems" :pagination="true"
    :gridOptions="tableOptions" :quickFilterText="quickFilter"></ag-grid-vue>
</template>
<script setup="props">

import { AgGridVue } from "ag-grid-vue3";
import { ref, defineProps, computed, watch, defineEmits, onBeforeUnmount, nextTick } from "vue";
import { prepareTableGridOptions } from "./prepareTableGridOptions.js";
import { sanitizeColDefs } from "./sanitizeColDefs.js";
import { createColumnTypes } from "./tableColumnTypes.js";
// event emiters
const emit = defineEmits(["itemSelected", "optionsChanged", "gridReady", "selectionChanged"]);

// refernce for the table
const table = ref(null);
const tableApi = ref({});
const gridApi = ref({});
const columnApi = ref({});

// Setup prop
const props = defineProps({
  items: Array,
  quickFilter: String,
  paginationPageSize: Number,
  tableSettings: Object,
  paginationAutoPageSize: Boolean,
  rowDrag: Boolean,
  rowDragFn: Function,
});

const tableSettingsWithColumnStateRemoved = computed(() => {
  let tableSettings = { ...props.tableSettings };
  delete tableSettings.columnState;
  return tableSettings;
});

const citems = computed(() => {
  return props.items;
});

const tableOptions = ref(prepareTableGridOptions({
  onRowClicked(row) {
    emit("itemSelected", row);
  },
  onSelectionChanged(event) {
    emit("selectionChanged", event.api.getSelectedRows());
  },
  onGridReady(params) {
    tableApi.value = params.api;
    columnApi.value = params.api;
    gridApi.value = params?.api?.filterManager?.gridOptionsService?.api || params?.api?.filterManager?.gos?.api;
    
    // Force initial data load
    if (props.items?.length > 0) {
      params.api.setGridOption('rowData', props.items);
    }
    
    setTableSettings();
    emit("gridReady", params.api);
  },
  getRowStyle(params) {
    if (params.node.rowIndex % 2 === 1) {
      return { background: "#eee" };
    }
  },
  onFilterChanged() {
    
    changeMade();
  },
  onSortChanged() {
    changeMade();
  },
  onColumnVisible(params) {
    
    
    changeMade();
  },
  onColumnPinned() {
    changeMade();
  },
  onColumnResized() {
    changeMade();
  },
  onColumnMoved() {
    changeMade();
  },
  onColumnValueChanged() {
    changeMade();
  },
  onPaginationChanged() {
    changeMade();
  },
  onFirstDataRendered() {
    setTableSettings();
  },
  defaultColDef: {
    sortable: true,
    filter: true,
    resizable: true,
    cellDataType: false,
    minWidth: 100,
    suppressFieldDotNotation: false,
    useValueFormatterForExport: true,
    useValueParserForImport: true,

    ...(props.rowDrag ? { rowDrag: (params) => {
      const cols = params.api.getAllDisplayedColumns();
      return cols.indexOf(params.column) === 0;
    }} : {}),

    enableFilterHandlers: true,
    // dndSource: props.rowDrag || false,
    dndSourceOnRowDrag: props.rowDragFn || false,
  },
  rowHeight: 36,
  enableFilterHandlers: true,
  paginationPageSize: props.paginationPageSize || 15,
  paginationAutoPageSize: props.paginationAutoPageSize || false,
  suppressFieldDotNotation: false,
  ...(props.rowDrag ? { rowDragManaged: true } : {}),
  columnDefs: (() => {
    const cols = [...(props?.tableSettings?.columnState || [])];
    if (props.rowDrag && cols.length > 0) {
      cols[0] = { ...cols[0], rowDrag: true };
    }
    return cols;
  })(),
  columnTypes: createColumnTypes(),
  ...tableSettingsWithColumnStateRemoved.value,
}));


const setTableSettings = () => {
  
  if (props?.tableSettings?.columnState?.length > 0 && columnApi.value) {
    // columnApi.value.applyColumnState({
    //   state: props.tableSettings?.columnState,
    // });
  }
  if (props?.tableSettings?.filter && tableApi.value?.setFilterModel) {
    tableApi.value.setFilterModel(props?.tableSettings?.filter);
  }
  if (typeof props?.tableSettings?.page === 'number' && tableApi.value?.paginationGoToPage) {
    tableApi.value.paginationGoToPage(props.tableSettings.page);
  }
};

watch(
  () => props.quickFilter,
  (newValue) => {
    if (tableApi.value.setGridOption) {
      tableApi.value.setGridOption('quickFilterText', newValue);
    }
  }
);
watch(
  () => props.items,
  (newValue) => {
    nextTick(() => {
      try {
        if (tableApi.value?.setGridOption) {
          const pageToRestore = typeof tableApi.value.paginationGetCurrentPage === 'function'
            ? tableApi.value.paginationGetCurrentPage()
            : null;
          tableApi.value.setGridOption('rowData', newValue);
          if (pageToRestore > 0 && tableApi.value?.paginationGoToPage) {
            nextTick(() => tableApi.value.paginationGoToPage(pageToRestore));
          }
        }
      } catch (error) {
        console.error("Error updating table data:", error);
      }
    });
  },
  { immediate: true, deep: true }
);

watch(
  () => props.tableSettings?.columnState,
  (newColumnState) => {
    if (tableApi.value?.setGridOption && newColumnState?.length > 0) {

      
      tableApi.value.setGridOption('columnDefs', sanitizeColDefs(newColumnState));
    }
  },
  { deep: true }
);


const destroy = () => {
  try {

    tableApi.value = null;
    columnApi.value = null;
    gridApi.value = null;
    tableApi.value.destroy();
  } catch (error) {
    
  }
}

const download = () => {
  
  props.tableSettings.exportParams ? tableApi.value.exportDataAsCsv(props.tableSettings.exportParams)  : tableApi.value.exportDataAsCsv();
};

var to;
const changeMade = () => {
  clearTimeout(to);

  to = setTimeout(() => {
    
    
    emit("optionsChanged");
    // setTimeout(setTableSettings, 100);
  }, 100);
};

onBeforeUnmount(() => {
  destroy();
});

defineExpose({
  download,
  tableApi,
  columnApi,
  table,
  setTableSettings
});
</script>
